import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import GlobalSearch from './GlobalSearch';
import { T, FONT_BODY, FONT_MONO } from './styleEditorial';

// COLORS = alias éditorial — palette unifiée encre + papier + or.
// Toutes les pages secondaires (Valorisation, Previsionnel, etc.) et le Simulator
// importent ce token et basculent automatiquement sur l'identité visuelle uniforme.
export const COLORS = {
  blue:       T.ink,          // bleu marine → encre noir (principal)
  green:      T.gold,          // vert SaaS → or sourd (success/accents)
  dark:       T.ink,          // bleu foncé → encre noir
  light:      T.paperWarm,     // bleu pâle → papier crème warm
  lightGreen: T.paperWarm,     // vert pâle → papier crème warm
};

// ── Styles éditoriaux du menu mobile (angles carrés, filets nets, aucun dégradé) ──
const M_LINK = {
  display: 'block', padding: '13px 14px', minHeight: '44px', boxSizing: 'border-box',
  background: T.paperWhite, border: `1px solid ${T.ruleLight}`, marginBottom: '-1px',
  color: T.ink, textDecoration: 'none', fontWeight: 500, fontSize: '14px', fontFamily: FONT_BODY,
};
const M_LABEL = {
  fontFamily: FONT_BODY, fontSize: '12px', fontWeight: 600, letterSpacing: '0.16em',
  textTransform: 'uppercase', color: T.goldDeep, marginTop: '22px', marginBottom: '10px',
};
const M_CTA = {
  display: 'block', textAlign: 'center', padding: '16px', minHeight: '48px', boxSizing: 'border-box',
  background: T.ink, color: T.paper, border: `1px solid ${T.ink}`, textDecoration: 'none',
  fontSize: '13px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase',
  marginTop: '22px', fontFamily: FONT_BODY,
};

export function Logo({ height = 36, light = false }) {
  return (
    <svg height={height} viewBox="0 0 280 70" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
      <defs>
        <linearGradient id="sharedLogoBrand" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={light ? '#fff' : COLORS.blue}/>
          <stop offset="100%" stopColor={light ? '#fff' : COLORS.green}/>
        </linearGradient>
        <linearGradient id="sharedLogoIcon" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={COLORS.dark}/>
          <stop offset="100%" stopColor={COLORS.green}/>
        </linearGradient>
      </defs>
      <g transform="translate(8,12)">
        <rect x="0" y="0" width="46" height="46" rx="10" fill={light ? 'rgba(255,255,255,0.15)' : 'url(#sharedLogoIcon)'}/>
        <path d="M10 32 L18 22 L26 28 L36 14" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="36" cy="14" r="3.5" fill="white"/>
      </g>
      <text x="66" y="36" fontFamily="-apple-system, Segoe UI, Roboto, sans-serif" fontSize="26" fontWeight="700" fill="url(#sharedLogoBrand)">FinAnalyse</text>
      <text x="66" y="54" fontFamily="-apple-system, Segoe UI, Roboto, sans-serif" fontSize="9" fontWeight="500" fill={light ? 'rgba(255,255,255,0.7)' : '#888'} letterSpacing="1.5">EXPERTISE FINANCIÈRE PME</text>
    </svg>
  );
}

export function Nav() {
  const [servicesOpen, setServicesOpen] = React.useState(false);
  const [cabinetOpen, setCabinetOpen] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Close mobile menu on route change
  React.useEffect(() => {
    const handler = () => setMobileOpen(false);
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  return (
    <>
      <nav style={{ padding: '18px 32px', borderBottom: `1px solid ${T.ruleLight}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: T.paper, zIndex: 100 }}>
        {/* aria-label : le logo est un SVG, sans lui le lien n'a aucun nom accessible
            (constat Lighthouse « Links do not have a discernible name », audit 2026-08-15). */}
        <Link to="/" aria-label="FinAnalyse — retour à l’accueil" style={{ textDecoration: 'none' }}><Logo height={36} /></Link>

        {/* Desktop nav — éditoriale, sans icônes Lucide superflues */}
        <div className="nav-links" style={{ display: 'flex', gap: '32px', fontSize: '13px', alignItems: 'center', fontFamily: FONT_BODY }}>

          {/* Dropdown CABINET — en premier */}
          <div style={{ position: 'relative' }} onMouseEnter={() => setCabinetOpen(true)} onMouseLeave={() => setCabinetOpen(false)}>
            <span className="nav-link-ed" style={{ color: T.ink, cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '6px', paddingBottom: '2px', position: 'relative' }}>
              Cabinet <ChevronDown size={12} style={{ opacity: 0.5 }} />
            </span>
            {cabinetOpen && (
              <div style={{ position: 'absolute', top: '100%', left: '-12px', paddingTop: '12px', minWidth: '280px' }}>
                <div style={{ background: T.paperWhite, border: `1px solid ${T.ink}`, padding: '6px', overflow: 'hidden' }}>
                  {[
                    { to: '/methodologie',  label: 'Notre méthode',     sub: '3 méthodes M&A' },
                    { to: '/processus',     label: 'Notre processus',   sub: '6 étapes · 48 h' },
                    { to: '/a-propos',      label: 'À propos',          sub: 'Le fondateur' },
                    { to: '/etudes-de-cas', label: 'Études de cas',     sub: 'Cas pratiques anonymisés' },
                  ].map(c => (
                    <Link key={c.to} to={c.to} style={{ display: 'block', padding: '11px 14px', textDecoration: 'none', transition: 'background 0.15s', fontFamily: FONT_BODY }}
                      onMouseEnter={e => e.currentTarget.style.background = T.paperWarm}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div style={{ fontSize: '13.5px', fontWeight: 500, color: T.ink }}>{c.label}</div>
                      <div style={{ fontSize: '13px', color: T.muted, marginTop: '2px', fontStyle: 'italic' }}>{c.sub}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Dropdown SERVICES */}
          <div style={{ position: 'relative' }} onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
            <span className="nav-link-ed" style={{ color: T.ink, cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '6px', paddingBottom: '2px', position: 'relative' }}>
              Services <ChevronDown size={12} style={{ opacity: 0.5 }} />
            </span>
            {servicesOpen && (
              <div style={{ position: 'absolute', top: '100%', left: '-12px', paddingTop: '12px', minWidth: '260px' }}>
                <div style={{ background: T.paperWhite, border: `1px solid ${T.ink}`, padding: '6px', overflow: 'hidden' }}>
                  {[
                    { to: '/valorisation',     label: 'Valorisation',       price: '900 €' },
                    { to: '/previsionnel',     label: 'Prévisionnel',       price: '500 €' },
                    { to: '/business-plan',    label: 'Business Plan',      price: '700 €' },
                    { to: '/audit-tresorerie', label: 'Audit trésorerie',   price: '350 €' },
                    { to: '/cession',          label: 'Dossier cession',    price: '1 200 €' },
                    { to: '/tableau-de-bord',  label: 'Tableau de bord',    price: '150 €/mois' },
                  ].map(s => (
                    <Link key={s.to} to={s.to} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', padding: '11px 14px', textDecoration: 'none', transition: 'background 0.15s', fontFamily: FONT_BODY }}
                      onMouseEnter={e => e.currentTarget.style.background = T.paperWarm}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ fontSize: '13.5px', fontWeight: 500, color: T.ink }}>{s.label}</span>
                      <span style={{ fontFamily: "'JetBrains Mono', 'JetBrains Mono Fallback', monospace", fontSize: '13px', color: T.goldDeep, fontWeight: 500 }}>{s.price}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link to="/tarifs" className="nav-link-ed" style={{ color: T.ink, textDecoration: 'none', fontWeight: 500, paddingBottom: '2px', position: 'relative' }}>
            Honoraires
          </Link>
          <Link to="/faq" className="nav-link-ed" style={{ color: T.ink, textDecoration: 'none', fontWeight: 500, paddingBottom: '2px', position: 'relative' }}>
            FAQ
          </Link>
          <Link to="/blog" className="nav-link-ed" style={{ color: T.ink, textDecoration: 'none', fontWeight: 500, paddingBottom: '2px', position: 'relative' }}>
            Blog
          </Link>
          <GlobalSearch />
          <Link to="/diagnostic-gratuit" className="nav-cta-ed" style={{ padding: '11px 22px', background: T.ink, color: T.paper, textDecoration: 'none', fontSize: '13px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', border: `1px solid ${T.ink}`, transition: 'background 0.25s, color 0.25s' }}>
            Diagnostic offert
          </Link>
        </div>

        {/* Mobile burger */}
        <button className="nav-mobile-cta" aria-label="Menu" aria-expanded={mobileOpen} onClick={() => setMobileOpen(!mobileOpen)}
          style={{ display: 'none', background: 'transparent', border: 'none', cursor: 'pointer', padding: '10px', flexDirection: 'column', gap: '4px', minWidth: '44px', minHeight: '44px', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ width: '24px', height: '2.5px', background: '#0a1929', borderRadius: '2px', transition: 'all 0.25s', transform: mobileOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none' }} />
          <span style={{ width: '24px', height: '2.5px', background: '#0a1929', borderRadius: '2px', transition: 'all 0.25s', opacity: mobileOpen ? 0 : 1 }} />
          <span style={{ width: '24px', height: '2.5px', background: '#0a1929', borderRadius: '2px', transition: 'all 0.25s', transform: mobileOpen ? 'rotate(-45deg) translate(6px, -6px)' : 'none' }} />
        </button>
      </nav>

      {/* Mobile menu (slide down) */}
      {mobileOpen && (
        <div role="dialog" aria-modal="true" aria-label="Menu de navigation" style={{ position: 'fixed', top: '64px', left: 0, right: 0, bottom: 0, background: 'rgba(10,25,41,0.5)', zIndex: 99, animation: 'fadeUp 0.2s ease-out' }} onClick={() => setMobileOpen(false)}>
          <div style={{ background: T.paper, padding: '1.5rem', maxHeight: 'calc(100vh - 64px)', overflowY: 'auto', borderTop: `1px solid ${T.ink}` }} onClick={e => e.stopPropagation()}>
            <div style={{ ...M_LABEL, marginTop: 0 }}>Navigation</div>
            <Link to="/" onClick={() => setMobileOpen(false)} style={M_LINK}>Accueil</Link>

            <div style={M_LABEL}>Cabinet</div>
            {[
              { to: '/methodologie', label: 'Notre méthode' },
              { to: '/processus', label: 'Notre processus' },
              { to: '/a-propos', label: 'À propos' },
              { to: '/etudes-de-cas', label: 'Études de cas' },
            ].map(l => (
              <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} style={M_LINK}>{l.label}</Link>
            ))}

            <div style={M_LABEL}>Ressources</div>
            {[
              { to: '/tarifs', label: 'Honoraires' },
              { to: '/glossaire', label: 'Glossaire' },
              { to: '/blog', label: 'Blog' },
            ].map(l => (
              <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} style={M_LINK}>{l.label}</Link>
            ))}

            <div style={M_LABEL}>Services</div>
            {[
              { to: '/valorisation', label: 'Valorisation', price: '900 €' },
              { to: '/previsionnel', label: 'Prévisionnel', price: '500 €' },
              { to: '/business-plan', label: 'Business Plan', price: '700 €' },
              { to: '/audit-tresorerie', label: 'Audit Trésorerie', price: '350 €' },
              { to: '/cession', label: 'Dossier Cession', price: '1 200 €' },
            ].map(s => (
              <Link key={s.to} to={s.to} onClick={() => setMobileOpen(false)} style={{ ...M_LINK, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{s.label}</span>
                <span style={{ fontFamily: FONT_MONO, fontSize: '13px', color: T.goldDeep }}>{s.price}</span>
              </Link>
            ))}

            <div style={M_LABEL}>Outils gratuits</div>
            {[
              { to: '/outils/calcul-ebitda', label: 'Calculateur EBITDA' },
              { to: '/outils/calcul-bfr', label: 'Calculateur BFR' },
              { to: '/outils/calcul-dscr', label: 'Calculateur DSCR' },
              { to: '/outils/capacite-emprunt', label: 'Capacité d\'emprunt' },
              { to: '/outils/pacte-dutreil', label: 'Pacte Dutreil' },
              { to: '/outils/fonds-de-commerce', label: 'Fonds de commerce' },
              { to: '/#simulateur', label: 'Estimation valorisation' },
            ].map(t => (
              <Link key={t.to} to={t.to} onClick={() => setMobileOpen(false)} style={M_LINK}>{t.label}</Link>
            ))}

            <Link to="/contact" onClick={() => setMobileOpen(false)} style={M_CTA}>
              Nous contacter
            </Link>
          </div>
        </div>
      )}

      {/* Cible du lien d'évitement « Aller au contenu principal » (index.js).
          Elle était déclarée mais n'existait nulle part sauf sur /faq : le lien
          ne menait donc à rien (constat Lighthouse « Skip links are not focusable »,
          audit 2026-08-15). Placée APRÈS la navigation, elle fait vraiment sauter
          le menu. tabIndex=-1 la rend ciblable par le focus sans l'ajouter à
          l'ordre de tabulation. */}
      <div id="main-content" tabIndex={-1} />
    </>
  );
}

// Breadcrumbs (fil d'Ariane) — bon pour SEO et UX
export function Breadcrumbs({ items }) {
  // items = [{ label, to }]  Le dernier item n'a pas de lien (page courante)
  return (
    <nav aria-label="Fil d'Ariane" style={{ padding: '2px 2rem', background: T.paperWarm, borderBottom: `1px solid ${T.ruleLight}` }}>
      <ol style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', gap: '6px', listStyle: 'none', padding: 0, fontSize: '13px', fontFamily: FONT_BODY }}>
        {items.map((item, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {item.to ? (
              <Link to={item.to} style={{ display: 'inline-block', padding: '12px 2px', color: COLORS.blue, textDecoration: 'none', fontWeight: '500' }}>{item.label}</Link>
            ) : (
              <span style={{ display: 'inline-block', padding: '12px 2px', color: T.inkSoft, fontWeight: '600' }} aria-current="page">{item.label}</span>
            )}
            {i < items.length - 1 && <span style={{ color: T.muted }} aria-hidden="true">›</span>}
          </li>
        ))}
      </ol>

      {/* JSON-LD BreadcrumbList pour Google — « < » échappé pour éviter toute injection de balise */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': items.map((item, i) => ({
          '@type': 'ListItem',
          'position': i + 1,
          'name': item.label,
          ...(item.to ? { 'item': `https://www.finanalyse.fr${item.to}` } : {}),
        })),
      }).replace(/</g, '\\u003c') }} />
    </nav>
  );
}

// Bouton "retour en haut" qui apparaît après scroll — carré éditorial, sans ombre
export function BackToTop() {
  const [show, setShow] = React.useState(false);

  React.useEffect(() => {
    const handler = () => setShow(window.scrollY > 600);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  if (!show) return null;

  return (
    <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="Retour en haut de page"
      style={{
        position: 'fixed', bottom: '160px', right: '24px', zIndex: 9990,
        width: '46px', height: '46px', borderRadius: 0,
        background: T.paper, color: T.ink,
        border: `1px solid ${T.ink}`,
        cursor: 'pointer', fontSize: '20px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background 0.2s ease, color 0.2s ease',
        animation: 'fadeUp 0.3s ease-out',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = T.ink; e.currentTarget.style.color = T.paper; }}
      onMouseLeave={e => { e.currentTarget.style.background = T.paper; e.currentTarget.style.color = T.ink; }}>
      ↑
    </button>
  );
}

// ─── Footer « colophon » — fond encre, texte papier, maillage SEO inchangé ────
// Libellés sans émojis ni prix (les prix vivent sur /tarifs).
const FOOTER_COLS = [
  {
    title: 'Outils gratuits',
    links: [
      { to: '/#simulateur', label: 'Estimation valorisation' },
      { to: '/diagnostic-gratuit', label: 'Diagnostic financier' },
      { to: '/outils/calcul-ebitda', label: 'Calculateur EBITDA' },
      { to: '/outils/calcul-bfr', label: 'Calculateur BFR' },
      { to: '/outils/calcul-dscr', label: 'Calculateur DSCR' },
      { to: '/outils/capacite-emprunt', label: 'Capacité d\'emprunt' },
      { to: '/outils/pacte-dutreil', label: 'Simulateur Pacte Dutreil' },
      { to: '/outils/fonds-de-commerce', label: 'Valeur fonds de commerce' },
    ],
  },
  {
    title: 'Services',
    links: [
      { to: '/valorisation', label: 'Valorisation' },
      { to: '/previsionnel', label: 'Prévisionnel' },
      { to: '/business-plan', label: 'Business Plan' },
      { to: '/audit-tresorerie', label: 'Audit Trésorerie' },
      { to: '/cession', label: 'Cession' },
    ],
  },
  {
    title: 'Par secteur',
    links: [
      { to: '/valorisation/restaurant', label: 'Restauration / CHR' },
      { to: '/valorisation/btp', label: 'BTP / Artisanat' },
      { to: '/valorisation/saas', label: 'Tech / SaaS' },
      { to: '/valorisation/commerce', label: 'Commerce / Retail' },
      { to: '/valorisation/industrie', label: 'Industrie' },
      { to: '/valorisation/sante', label: 'Santé / Bien-être' },
      { to: '/valorisation/immobilier', label: 'Immobilier' },
      { to: '/sitemap', label: 'Plan du site' },
    ],
  },
  {
    title: 'Par ville',
    links: [
      { to: '/valorisation/ville/paris', label: 'Paris' },
      { to: '/valorisation/ville/lyon', label: 'Lyon' },
      { to: '/valorisation/ville/marseille', label: 'Marseille' },
      { to: '/valorisation/ville/toulouse', label: 'Toulouse' },
      { to: '/valorisation/ville/bordeaux', label: 'Bordeaux' },
      { to: '/valorisation/ville/lille', label: 'Lille' },
      { to: '/valorisation/ville/nantes', label: 'Nantes' },
      { to: '/sitemap', label: '+ 8 autres villes' },
    ],
  },
  {
    title: 'Ressources',
    links: [
      { to: '/blog', label: 'Blog' },
      { to: '/glossaire', label: 'Glossaire' },
      { to: '/etudes-de-cas', label: 'Études de cas' },
      { to: '/quiz', label: 'Quiz services' },
      { to: '/avis-clients', label: 'Avis clients' },
      { to: '/combien-vaut', label: 'Combien vaut [secteur] ?' },
      { to: '/tarifs', label: 'Tarifs' },
      { to: '/a-propos', label: 'À propos' },
      { to: '/contact', label: 'Contact' },
    ],
  },
];

// Papier translucide sur encre — tous ≥ 5:1 de contraste sur T.ink
const FOOT_LINK_DIM = 'rgba(250,248,243,0.78)';
const FOOT_TEXT_DIM = 'rgba(250,248,243,0.72)';
const FOOT_LEGAL_DIM = 'rgba(250,248,243,0.65)';
const FOOT_RULE = 'rgba(255,255,255,0.15)';

const footLinkStyle = {
  display: 'block',
  padding: '12px 0',            // + lineHeight 20px = cible tactile 44px
  fontSize: '14px',
  lineHeight: '20px',
  fontFamily: FONT_BODY,
  color: FOOT_LINK_DIM,
  textDecoration: 'none',
  transition: 'color 0.2s ease',
};

const footLegalStyle = {
  display: 'inline-block',
  padding: '12px 0',
  fontSize: '13px',
  lineHeight: '20px',
  fontFamily: FONT_BODY,
  color: FOOT_LEGAL_DIM,
  textDecoration: 'none',
  transition: 'color 0.2s ease',
};

const footHover = e => { e.currentTarget.style.color = T.paper; };
const footUnhoverLink = e => { e.currentTarget.style.color = FOOT_LINK_DIM; };
const footUnhoverLegal = e => { e.currentTarget.style.color = FOOT_LEGAL_DIM; };

export function Footer() {
  const colTitle = { fontSize: '13px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.goldSoft, fontFamily: FONT_BODY, margin: '0 0 6px' };

  return (
    <footer style={{ padding: '4rem 2rem 2rem', background: T.ink, borderTop: `1px solid ${T.gold}`, fontFamily: FONT_BODY }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '2.5rem 2rem', marginBottom: '2.5rem' }}>
          <div>
            <div style={{ marginBottom: '16px' }}><Logo height={40} light /></div>
            <p style={{ fontSize: '13px', color: FOOT_TEXT_DIM, lineHeight: '1.7', margin: 0 }}>Cabinet d'analyse financière pour PME françaises. Méthodes M&A, benchmarks Banque de France, révision humaine. Rapports détaillés en 48h.</p>
          </div>
          {FOOTER_COLS.map(col => (
            <div key={col.title}>
              {/* h3 et non h4 : les titres de colonnes du pied de page suivaient
                  des h2 sans h3 intermédiaire, ce qui casse la navigation par
                  titres au lecteur d'écran (audit 2026-08-15). */}
              <h3 style={colTitle}>{col.title}</h3>
              {col.links.map(l => (
                <Link key={l.to + l.label} to={l.to} style={footLinkStyle} onMouseEnter={footHover} onMouseLeave={footUnhoverLink}>{l.label}</Link>
              ))}
            </div>
          ))}
          <div>
            <h3 style={colTitle}>Contact</h3>
            <a href="mailto:contact@finanalyse.fr" style={footLinkStyle} onMouseEnter={footHover} onMouseLeave={footUnhoverLink}>contact@finanalyse.fr</a>
            <p style={{ fontSize: '13px', color: FOOT_TEXT_DIM, lineHeight: '2', margin: '4px 0 0' }}>
              Réponse sous 24h (Lun–Ven)<br />
              France entière · 100% en ligne
            </p>
          </div>
        </div>
        <div style={{ borderTop: `1px solid ${FOOT_RULE}`, paddingTop: '4px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0 1.5rem', alignItems: 'center' }}>
          <p style={{ fontSize: '13px', color: FOOT_LEGAL_DIM, margin: 0, padding: '12px 0' }}>© 2026 FinAnalyse — Cabinet d'analyse financière PME</p>
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            <Link to="/mentions-legales" style={footLegalStyle} onMouseEnter={footHover} onMouseLeave={footUnhoverLegal}>Mentions légales</Link>
            <Link to="/cgv" style={footLegalStyle} onMouseEnter={footHover} onMouseLeave={footUnhoverLegal}>CGV</Link>
            <Link to="/confidentialite" style={footLegalStyle} onMouseEnter={footHover} onMouseLeave={footUnhoverLegal}>Confidentialité (RGPD)</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
